<ng-container>
    @if(isPageRestrictionLoaded){
    <div class="ppl-container-body">
        <div class="ppl-container-head">
            <h1 class="page-title-accessibility">Spending Plans</h1>
            <div class="action">
                <ppl-button label="View Authorizations" icon="fa fa-list" [customClass]="'ghost-button'"
                    [iconPosition]="'left'" (click)="onViewAuthorizations()"></ppl-button>
                @if(!isReadOnly){
                <ppl-button label="Add Authorizations" icon="fa fa-plus" [customClass]="'primary-button'" [styleString]="'margin-left: 8px;'"
                    [iconPosition]="'left'" (click)="onAddAuthorizations()"
                    [authorizeButton]="[pageId, 'AddAuthorizations']"></ppl-button>
                }
            </div>
        </div>
        <div class="ppl-container">
            <div class="ppl-content-container">
                <div class="table-style">
                    <div class="plans-toolbar">
                        <div class="plans-search">
                            <label class="visually-hidden" for="spendingPlanSearch">Search spending plans</label>
                            <i class="fa fa-search" aria-hidden="true"></i>
                            <input id="spendingPlanSearch" type="text" class="plans-search-input"
                                placeholder="Search plan, participant, UCI ID, PPL ID, coordinator..."
                                [value]="searchTerm" (input)="onSearch($any($event.target).value)" />
                        </div>
                        <div class="plans-toolbar-actions">
                            <button type="button" class="link-button" (click)="expandAll()">Expand all</button>
                            <button type="button" class="link-button" (click)="collapseAll()">Collapse all</button>
                        </div>
                    </div>

                    <div class="plans-scroll">
                        <table class="plans-table">
                            <caption class="visually-hidden">
                                Spending plans, expandable to show authorizations, provider allocations and service
                                details
                            </caption>
                            <thead>
                                <tr>
                                    <th scope="col" class="col-toggle"><span class="visually-hidden">Expand</span></th>
                                    <th scope="col">Spending Plan</th>
                                    <th scope="col">Participant</th>
                                    <th scope="col">UCI ID</th>
                                    <th scope="col">Participant PPL ID</th>
                                    <th scope="col" class="num">Authorized</th>
                                    <th scope="col">Plan Term</th>
                                    <th scope="col">Service Coordinator</th>
                                    <th scope="col">Regional Center</th>
                                </tr>
                            </thead>
                            <tbody>
                                @for(plan of plans; track $index) {
                                <tr class="plan-row" [class.expanded]="plan.expanded"
                                    (click)="togglePlan(plan)">
                                    <td class="col-toggle">
                                        <button type="button" class="toggle-button" [attr.aria-expanded]="plan.expanded"
                                            [attr.aria-label]="(plan.expanded ? 'Collapse ' : 'Expand ') + plan.clientAuth"
                                            (click)="$event.stopPropagation(); togglePlan(plan)">
                                            <i class="fa" [class.fa-chevron-right]="!plan.expanded"
                                                [class.fa-chevron-down]="plan.expanded" aria-hidden="true"></i>
                                        </button>
                                    </td>
                                    <td class="strong">{{ plan.clientAuth }}</td>
                                    <td>{{ plan.consumerName }}</td>
                                    <td>{{ plan.medicaid }}</td>
                                    <td>{{ plan.consumerPPLId }}</td>
                                    <td class="num nowrap">{{ plan.authorized }}</td>
                                    <td class="nowrap">{{ plan.term }}</td>
                                    <td>{{ plan.serviceCoordinator }}</td>
                                    <td>{{ plan.orgName }}</td>
                                </tr>

                                @if(plan.expanded) {
                                <tr class="detail-row">
                                    <td [attr.colspan]="planColumnCount">
                                        <div class="detail-wrapper">
                                            @if(plan.detailsLoading) {
                                            <p class="detail-message">
                                                <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                                                Loading authorizations...
                                            </p>
                                            } @else if(plan.detailsError) {
                                            <p class="detail-message">
                                                Unable to load authorizations for {{ plan.clientAuth }}.
                                                <button type="button" class="link-button"
                                                    (click)="reloadDetails(plan)">Try again</button>
                                            </p>
                                            } @else if(plan.detailRows.length === 0) {
                                            <p class="detail-message">
                                                No authorizations on this spending plan.
                                            </p>
                                            } @else {
                                            <table class="detail-table">
                                                <caption class="visually-hidden">
                                                    Authorizations for {{ plan.clientAuth }}
                                                </caption>
                                                <thead>
                                                    <tr class="group-header">
                                                        <th scope="colgroup" colspan="6" class="group-auth">
                                                            Authorization
                                                        </th>
                                                        <th scope="colgroup" colspan="6" class="group-provider">
                                                            Provider Allocation
                                                        </th>
                                                        <th scope="colgroup" colspan="5" class="group-service">
                                                            Service Detail
                                                        </th>
                                                    </tr>
                                                    <tr>
                                                        <th scope="col" class="group-auth-start">Service Code</th>
                                                        <th scope="col">Term</th>
                                                        <th scope="col" class="num">Authorized</th>
                                                        <th scope="col" class="num">Spent</th>
                                                        <th scope="col" class="num">Remaining</th>
                                                        <th scope="col" class="num">Is Active</th>
                                                        <th scope="col" class="group-provider-start">Provider</th>
                                                        <th scope="col">Term</th>
                                                        <th scope="col" class="num">Allocated</th>
                                                        <th scope="col" class="num">Used</th>
                                                        <th scope="col" class="num">Remaining</th>
                                                        <th scope="col" class="num">Is Active</th>
                                                        <th scope="col" class="group-service-start">Detail</th>
                                                        <th scope="col">Frequency</th>
                                                        <th scope="col" class="num">Units</th>
                                                        <th scope="col" class="num">Price</th>
                                                        <th scope="col" class="num">Is Active</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    @for(row of plan.detailRows; track $index) {
                                                    <tr [class.auth-start]="!!row.auth"
                                                        [class.allocation-end]="row.isAllocationEnd">
                                                        @if(row.auth) {
                                                        <td [attr.rowspan]="row.authRowSpan"
                                                            class="span-cell group-auth-start">
                                                            <div class="cell-primary">{{ row.auth.codeDesc }}</div>
                                                            <div class="cell-secondary">
                                                                {{ row.auth.serviceDescription }}
                                                            </div>
                                                            <div class="cell-meta">Source: {{ row.auth.sourceValue }}</div>
                                                            @if(row.auth.isDeactivated) {
                                                            <div class="cell-meta warn">Deactivated</div>
                                                            }
                                                            <button type="button" class="link-button"
                                                                (click)="onViewAuthorization(row.auth)">
                                                                <i class="fa fa-eye" aria-hidden="true"></i> View
                                                            </button>
                                                        </td>
                                                        <td [attr.rowspan]="row.authRowSpan"
                                                            class="span-cell nowrap">{{ row.auth.term }}</td>
                                                        <td [attr.rowspan]="row.authRowSpan"
                                                            class="span-cell num">{{ row.auth.authorized }}</td>
                                                        <td [attr.rowspan]="row.authRowSpan"
                                                            class="span-cell num">{{ row.auth.spent }}</td>
                                                        <td [attr.rowspan]="row.authRowSpan"
                                                            class="span-cell num">{{ row.auth.remaining }}</td>
                                                        <td [attr.rowspan]="row.authRowSpan"
                                                            class="span-cell num">{{ row.auth.isDeactivated ? 'No' : 'Yes' }}</td>
                                                        }

                                                        @if(row.allocation) {
                                                        <td [attr.rowspan]="row.allocationRowSpan"
                                                            class="span-cell group-provider-start"
                                                            [class.muted]="row.allocation.isPlaceholder">
                                                            <div class="cell-primary">
                                                                {{ row.allocation.pplProviderId }}
                                                                @if(row.allocation.isUnassigned) {
                                                                <span class="chip">Unassigned</span>
                                                                }
                                                            </div>
                                                            @if(row.allocation.providerName) {
                                                            <div class="cell-secondary">
                                                                {{ row.allocation.providerName }}
                                                            </div>
                                                            }
                                                        </td>
                                                        <td [attr.rowspan]="row.allocationRowSpan"
                                                            class="span-cell nowrap">{{ row.allocation.term }}</td>
                                                        <td [attr.rowspan]="row.allocationRowSpan"
                                                            class="span-cell num">{{ row.allocation.allocated }}</td>
                                                        <td [attr.rowspan]="row.allocationRowSpan"
                                                            class="span-cell num">{{ row.allocation.used }}</td>
                                                        <td [attr.rowspan]="row.allocationRowSpan"
                                                            class="span-cell num">{{ row.allocation.remaining }}</td>
                                                        <td [attr.rowspan]="row.allocationRowSpan"
                                                            class="span-cell num">{{ row.allocation.isActive ? 'Yes' : 'No' }}</td>
                                                        }

                                                        <td class="group-service-start"
                                                            [class.muted]="row.serviceDetail.isPlaceholder">
                                                            {{ row.serviceDetail.serviceDetail }}
                                                        </td>
                                                        <td>{{ row.serviceDetail.frequencyLabel }}</td>
                                                        <td class="num">{{ row.serviceDetail.units }}</td>
                                                        <td class="num nowrap">{{ row.serviceDetail.priceDisplay }}</td>
                                                        <td class="num">{{ row.serviceDetail.isActive ? 'Yes' : 'No' }}</td>
                                                    </tr>
                                                    }
                                                </tbody>
                                            </table>
                                            }
                                        </div>
                                    </td>
                                </tr>
                                }
                                }

                                @if(isLoading) {
                                <tr>
                                    <td [attr.colspan]="planColumnCount" class="empty-message">
                                        <i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Loading spending
                                        plans...
                                    </td>
                                </tr>
                                } @else if(hasError) {
                                <tr>
                                    <td [attr.colspan]="planColumnCount" class="empty-message">
                                        Unable to load spending plans.
                                        <button type="button" class="link-button" (click)="retry()">Try again</button>
                                    </td>
                                </tr>
                                } @else if(plans.length === 0) {
                                <tr>
                                    <td [attr.colspan]="planColumnCount" class="empty-message">
                                        No spending plans found.
                                    </td>
                                </tr>
                                }
                            </tbody>
                        </table>
                    </div>

                    @if(plans.length > 0) {
                    <p-paginator styleClass="plans-paginator" [first]="first" [rows]="recordsPerPage"
                        [totalRecords]="totalRecords" [rowsPerPageOptions]="recordsPerPageOptions"
                        [showCurrentPageReport]="true" [currentPageReportTemplate]="pageReportTemplate"
                        (onPageChange)="onPageChange($event)">
                    </p-paginator>
                    }
                </div>
            </div>
        </div>
    </div>
    }
</ng-container>
